/**
 * Single source of truth for PARTY and FRACTION (Fraktion) display naming.
 *
 * See PARTY_FACTION_NAMING_STANDARD.md (repo root) for the approved rules:
 *  - Abbreviations are shown in GERMAN in every UI language (EN included).
 *  - Fractions use the `F-…` form (F-SVP, F-Mitte, …), never the cryptic
 *    official letter codes (V, RL, M-E, GL, S, G).
 *  - Hover shows the FULL name, localized to the UI language (EN falls back to
 *    the German full name, matching the German-abbreviation rule).
 *
 * Resolvers accept any raw variant the data layer emits — official faction
 * letter codes, localized party names, historic aliases — and normalize to the
 * one canonical entry, so every call site renders the same label.
 */

import type { UILang } from '../context/AppContext';

export interface Meta {
  /** German abbreviation shown as the badge text in every language. */
  abbr: string;
  /** Full name per language; EN reuses the German name (see standard). */
  full: Record<'de' | 'fr' | 'it', string>;
}

// ── Fractions (parliamentary groups) ────────────────────────────────────────
// Keyed by canonical F-abbr. `codes` lists the raw values the DB / API emit.
const FACTIONS: { abbr: string; codes: string[]; full: Meta['full'] }[] = [
  // `codes` also lists the full-name / historic / localized strings the data
  // layer emits (Meilisearch transcripts store faction FULL names; older rows
  // carry historic group names) so every raw value resolves to one F-abbr.
  { abbr: 'F-SVP',   codes: ['V', 'V2', 'Fraktion der Schweizerischen Volkspartei', 'Lega-Fraktion'], full: { de: 'SVP-Fraktion',          fr: 'Groupe UDC',              it: 'Gruppo UDC' } },
  { abbr: 'F-FDP',   codes: ['RL', 'FDP-Liberale Fraktion', 'Freisinnig-demokratische Fraktion', 'Liberale Fraktion'], full: { de: 'FDP-Liberale Fraktion', fr: 'Groupe libéral-radical',  it: 'Gruppo liberale radicale' } },
  { abbr: 'F-Mitte', codes: ['M', 'M-E', 'C', 'Die Mitte-Fraktion', 'Die Mitte-Fraktion. Die Mitte. EVP.', 'CVP-Fraktion', 'Evangelische Volkspartei-Fraktion', 'Fraktion CVP-EVP'], full: { de: 'Die Mitte-Fraktion',    fr: 'Le Groupe du Centre',     it: 'Il Gruppo del Centro' } },
  { abbr: 'F-GLP',   codes: ['GL', 'Grünliberale Fraktion'], full: { de: 'Grünliberale Fraktion', fr: "Groupe vert'libéral",     it: 'Gruppo verde liberale' } },
  { abbr: 'F-SP',    codes: ['S', 'Sozialdemokratische Fraktion'], full: { de: 'SP-Fraktion',           fr: 'Groupe socialiste',       it: 'Gruppo socialista' } },
  { abbr: 'F-Grüne', codes: ['G', 'Grüne Fraktion'], full: { de: 'Grüne Fraktion',        fr: 'Groupe des VERT-E-S',     it: 'Gruppo dei Verdi' } },
  { abbr: 'F-BDP',   codes: ['BD'],           full: { de: 'BDP-Fraktion',          fr: 'Groupe PBD',              it: 'Gruppo PBD' } },
  { abbr: 'Fraktionslos', codes: ['FRAKTIONSLOS', 'NA'], full: { de: 'Fraktionslos', fr: 'Non-inscrit',           it: 'Senza gruppo' } },
];

// ── Parties ──────────────────────────────────────────────────────────────────
// `aliases` normalizes localized names / legacy codes → the one canonical abbr.
const PARTIES: { abbr: string; aliases: string[]; full: Meta['full'] }[] = [
  { abbr: 'SVP',   aliases: ['UDC', 'Schweizerische Volkspartei'],                         full: { de: 'Schweizerische Volkspartei',       fr: 'Union démocratique du centre', it: 'Unione democratica di centro' } },
  { abbr: 'SP',    aliases: ['PS', 'Sozialdemokratische Partei', 'PSS'],                    full: { de: 'Sozialdemokratische Partei',       fr: 'Parti socialiste',             it: 'Partito socialista' } },
  { abbr: 'FDP',   aliases: ['FDP-Liberale', 'FDP.Die Liberalen', 'PLR', 'PLR.Les Libéraux-Radicaux'], full: { de: 'FDP.Die Liberalen',        fr: 'PLR.Les Libéraux-Radicaux',    it: 'PLR.I Liberali Radicali' } },
  { abbr: 'Mitte', aliases: ['Die Mitte', 'M-E', 'M', 'C', 'CVP', 'Le Centre', 'Il Centro', 'Alleanza del Centro'], full: { de: 'Die Mitte',                 fr: 'Le Centre',                    it: 'Alleanza del Centro' } },
  { abbr: 'Grüne', aliases: ['GRÜNE', 'GPS', 'Grüne Partei der Schweiz', 'Les VERT-E-S', 'Les Vert-e-s', 'Verts', 'PES', 'I Verdi'], full: { de: 'Grüne Partei der Schweiz', fr: 'Les Vert-e-s',            it: 'I Verdi' } },
  { abbr: 'GLP',   aliases: ['glp', 'PVL', 'Grünliberale Partei', "Parti vert'libéral"],   full: { de: 'Grünliberale Partei',              fr: "Parti vert'libéral",           it: 'Partito verde liberale' } },
  { abbr: 'EVP',   aliases: ['PEV', 'Evangelische Volkspartei'],                            full: { de: 'Evangelische Volkspartei',         fr: 'Parti évangélique',            it: 'Partito evangelico' } },
  { abbr: 'EDU',   aliases: ['UDF', 'Eidgenössisch-Demokratische Union'],                   full: { de: 'Eidgenössisch-Demokratische Union',fr: 'Union Démocratique Fédérale',  it: 'Unione Democratica Federale' } },
  { abbr: 'CSP',   aliases: ['PCS', 'Christlich-soziale Partei'],                           full: { de: 'Christlich-soziale Partei',        fr: 'Parti chrétien-social',        it: 'Partito cristiano sociale' } },
  { abbr: 'Lega',  aliases: ['lega', 'Lega dei Ticinesi'],                                  full: { de: 'Lega dei Ticinesi',                fr: 'Lega dei Ticinesi',            it: 'Lega dei Ticinesi' } },
  { abbr: 'MCR',   aliases: ['MCG', 'Mouvement Citoyens Romands'],                          full: { de: 'Mouvement Citoyens Romands',       fr: 'Mouvement Citoyens Romands',   it: 'Mouvement Citoyens Romands' } },
  { abbr: 'EàG',   aliases: ['EaG', 'Ensemble à Gauche'],                                   full: { de: 'Ensemble à Gauche',                fr: 'Ensemble à Gauche',            it: 'Ensemble à Gauche' } },
  { abbr: 'PdA',   aliases: ['PST', 'POP', 'PSL', 'Partei der Arbeit'],                     full: { de: 'Partei der Arbeit',                fr: 'Parti ouvrier et populaire',   it: 'Partito svizzero del lavoro' } },
];

// Lookup indices (case-insensitive on the raw value).
const norm = (s: string) => s.trim().toUpperCase();
const FACTION_BY_CODE = new Map<string, { abbr: string; full: Meta['full'] }>();
for (const f of FACTIONS) {
  FACTION_BY_CODE.set(norm(f.abbr), f);
  for (const c of f.codes) FACTION_BY_CODE.set(norm(c), f);
}
const PARTY_BY_ALIAS = new Map<string, { abbr: string; full: Meta['full'] }>();
for (const p of PARTIES) {
  PARTY_BY_ALIAS.set(norm(p.abbr), p);
  for (const a of p.aliases) PARTY_BY_ALIAS.set(norm(a), p);
}

/** Full name in the UI language; EN reuses the German name (naming standard). */
function pick(full: Meta['full'], lang: UILang): string {
  return lang === 'FR' ? full.fr : lang === 'IT' ? full.it : full.de;
}

/**
 * Resolve a raw faction value (letter code, F-abbr, or group name) to its
 * standardized abbreviation + full names. Language-agnostic — pass the result's
 * `full` to `localizedFullName`. Unknown values pass through as their own abbr.
 */
export function factionMeta(raw: string | null | undefined): Meta {
  const value = (raw ?? '').trim();
  if (!value) return { abbr: '', full: { de: '', fr: '', it: '' } };
  const hit = FACTION_BY_CODE.get(norm(value));
  if (hit) return { abbr: hit.abbr, full: hit.full };
  // Some sources prefix the code with the group word ("Fraktion V", "Groupe V",
  // "Gruppo V"). Strip it and retry on the remaining code.
  const stripped = value.replace(/^(Fraktion|Groupe|Gruppo)\s+/i, '').trim();
  if (stripped && stripped !== value) {
    const hit2 = FACTION_BY_CODE.get(norm(stripped));
    if (hit2) return { abbr: hit2.abbr, full: hit2.full };
  }
  return { abbr: value, full: { de: value, fr: value, it: value } };
}

/**
 * Resolve a raw party value (abbr, localized name, or legacy code) to its
 * canonical abbreviation + full names. Unknown values pass through.
 */
export function partyMeta(raw: string | null | undefined): Meta {
  const value = (raw ?? '').trim();
  if (!value) return { abbr: '', full: { de: '', fr: '', it: '' } };
  const hit = PARTY_BY_ALIAS.get(norm(value));
  if (hit) return { abbr: hit.abbr, full: hit.full };
  return { abbr: value, full: { de: value, fr: value, it: value } };
}

/** Convenience: localized full name for a faction, or '' if unknown. */
export function factionFullName(raw: string | null | undefined, lang: UILang): string {
  return pick(factionMeta(raw).full, lang);
}

/** Convenience: localized full name for a party, or '' if unknown. */
export function partyFullName(raw: string | null | undefined, lang: UILang): string {
  return pick(partyMeta(raw).full, lang);
}

export { pick as localizedFullName };
